import { Loader2, Network } from 'lucide-react';
import { useLatestNxtSpreadsheet, useNxtCross } from '../hooks';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';

const CLASSIFICATION_LABEL: Record<string, string> = {
  missing: 'CNPJ pendente no Complemento (BZ)',
  divergent: 'Alvará registrado · conferir cadastro',
  ambiguous: 'Ambíguo',
  found: 'Encontrado',
};

const MATCH_METHOD_LABEL: Record<string, string> = {
  'registered-number': 'CNPJ principal',
  alvara: 'Alvará',
  address: 'Endereço do tributário',
};

export function NxtCrossSummary({ spreadsheetId }: { spreadsheetId?: string } = {}) {
  const { data: spreadsheet, isLoading: loadingSpreadsheet } = useLatestNxtSpreadsheet();
  const { data: cross, isLoading: loadingCross, isError, refetch, isFetching } = useNxtCross(spreadsheetId || spreadsheet?.id);

  if (loadingSpreadsheet) {
    return (
      <div className="flex h-32 items-center justify-center rounded-lg border bg-card">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!spreadsheet) return null;

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b p-4">
        <Network className="h-4 w-4 text-blue-500" />
        <h3 className="text-sm font-semibold">Claro NXT · cobertura de Alvará de Funcionamento</h3>
        <span className="text-xs text-muted-foreground">
          Fonte do recorte histórico: {(cross?.spreadsheet ?? spreadsheet).originalName} · {cross?.summary.total ?? 0} marcados manualmente como ausentes. A contagem completa está abaixo.
        </span>
      </div>

      {isError ? <p role="alert" className="p-4">Falha ao atualizar a conferência. <Button variant="outline" onClick={()=>refetch()}>Tentar novamente</Button></p> : loadingCross || !cross ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="p-4">
          {cross.propertyDocsExport?.latest && <p className="mb-3 text-xs text-muted-foreground">Alvarás: exportação PropertyDocs de {new Date(cross.propertyDocsExport.latest.completedAt).toLocaleString('pt-BR')}.</p>}
          {['preparing', 'waiting', 'downloading'].includes(cross.propertyDocsExport?.phase || '') && <p role="status" className="mb-3 text-sm">Nova exportação de Alvarás em preparação. Esta conferência será atualizada ao concluir.</p>}
          {cross.propertyDocsExport?.phase === 'error' && <p role="alert" className="mb-3 text-sm text-destructive">Falha na atualização de Alvarás: {cross.propertyDocsExport.error}</p>}
          {cross.registration && <section className="mb-6 space-y-3 border-b pb-6">
            <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold">Contagem automática de CNPJs NXT por status</h3><Button variant="outline" size="sm" disabled={isFetching} onClick={()=>refetch()}>Atualizar contagem</Button></div>
            <p className="text-xs text-muted-foreground">Base: {cross.registration.sourceName} · importada em {new Date(cross.registration.uploadedAt).toLocaleString('pt-BR')} · conferência em {new Date(cross.generatedAt).toLocaleString('pt-BR')}. Atualiza a cada minuto com os dados sincronizados do PropertyDocs.</p>
            <p className="text-xs text-muted-foreground">Cada CNPJ conta uma vez em cada cenário. O vínculo do Alvará usa exclusivamente Complemento (Documento), coluna BZ. Alvarás com status Inexistente também contam como cadastro. CNPJs ausentes são compatibilizados pelo endereço do tributário para identificar a ordem e continuam pendentes de cadastro. Múltiplas ordens candidatas exigem conferência.</p>
            <div className="grid gap-4 lg:grid-cols-2">{[['Cadastro da unidade ou Alvará',cross.registration],['Somente cadastro no Alvará',cross.registration.permitOnly]].map(([title,value])=>{
              const count=value as NonNullable<typeof cross.registration>;
              return <div className="rounded border p-3" key={String(title)}><h4 className="mb-2 text-sm font-semibold">{String(title)}</h4><table className="w-full text-sm"><thead><tr><th className="text-left">Status da unidade</th><th>Cadastrado</th><th>Não cadastrado</th><th>Total</th></tr></thead><tbody>{count.byStatus.map(g=><tr key={g.status} className="border-t"><td className="py-2">{g.status}</td><td className="text-center">{g.registered}</td><td className="text-center">{g.missing}</td><td className="text-center">{g.total}</td></tr>)}</tbody><tfoot><tr className="border-t font-semibold"><td>Total Geral</td><td className="text-center">{count.registered}</td><td className="text-center">{count.missing}</td><td className="text-center">{count.total}</td></tr></tfoot></table><Button className="mt-3" size="sm" variant="outline" onClick={()=>{
                const wb=XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb,XLSX.utils.json_to_sheet([...count.byStatus,{status:'Total Geral',registered:count.registered,missing:count.missing,total:count.total}].map(r=>({'Status da unidade':r.status,Cadastrado:r.registered,'Não cadastrado':r.missing,Total:r.total}))),'Contagem');
                XLSX.utils.book_append_sheet(wb,XLSX.utils.json_to_sheet(count.rows.map(r=>({CNPJ:r.cnpj,Ordem:r.ordem,Status:r.status,Cadastro:r.registered?'Cadastrado':'Não cadastrado',Unidades:r.units.join(' | '),Observação:r.warning,Fonte:cross.registration!.sourceName,Conferência:cross.generatedAt}))),'CNPJs');
                XLSX.writeFile(wb,`nxt-${title==='Cadastro da unidade ou Alvará'?'cadastro':'alvara'}.xlsx`);
              }}>Exportar contagem e todos os CNPJs</Button></div>;
            })}</div>
          </section>}
          <p className="mb-3 text-xs text-muted-foreground">Cadastro é o CNPJ encontrado no Complemento (Documento). Para os pendentes, a ordem é localizada pelo endereço do tributário: logradouro, número e CEP ou município/UF. A unidade identificada pelo endereço continua com CNPJ pendente no Alvará.</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ['Base NXT completa', spreadsheet.summary.uniqueCnpjs],
              ['Recorte a conferir', cross.summary.total],
              ['Alvará localizado', cross.summary.found],
              ['Sem Alvará registrado', cross.summary.missing],
              ['Conferir cadastro', cross.summary.divergent],
              ['Ambíguos', cross.summary.ambiguous],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-xl font-semibold">{value}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end"><Button variant="outline" size="sm" onClick={()=>{
            const data=cross.samples.filter(s=>s.classification!=='found').map(s=>({Ordem:s.ordem||s.pdUnits.map(u=>u.identification).join(' | ')||'',CNPJ:s.cnpj,Identificação:s.identification,Situação:CLASSIFICATION_LABEL[s.classification],Unidades:s.pdUnits.map(u=>u.name).join(' | '),Vínculos:s.pdUnits.flatMap(u=>u.matchedBy).map(m=>MATCH_METHOD_LABEL[m]).join(' | '),Divergências:s.divergentFields?.join(', ')||''}));
            const wb=XLSX.utils.book_new();XLSX.utils.book_append_sheet(wb,XLSX.utils.json_to_sheet(data),'NXT - pendências');XLSX.writeFile(wb,'nxt-pendencias-alvara.xlsx');
          }}>Exportar pendências NXT</Button></div>

          <div className="mt-4 max-h-72 overflow-y-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2">Ordem</th>
                  <th className="px-3 py-2">CNPJ</th>
                  <th className="px-3 py-2">Classificação</th>
                  <th className="px-3 py-2">Unidade PropertyDocs</th>
                  <th className="px-3 py-2">Vínculo identificado</th>
                  <th className="px-3 py-2">Divergências</th>
                </tr>
              </thead>
              <tbody>
                {cross.samples
                  .filter((sample) => sample.classification !== 'found')
                  .map((sample) => (
                    <tr key={sample.cnpj} className="border-b align-top text-xs">
                      <td className="px-3 py-2">{sample.ordem || sample.pdUnits.map(u=>u.identification).join(', ') || 'Não informada'}</td>
                      <td className="px-3 py-2 tabular-nums">
                        {sample.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
                      </td>
                      <td className="px-3 py-2">{CLASSIFICATION_LABEL[sample.classification] ?? sample.classification}</td>
                      <td className="px-3 py-2">
                        {sample.pdUnits.map((unit) => unit.identification || unit.name).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2">
                        {[...new Set(sample.pdUnits.flatMap(unit => unit.matchedBy))].map(method => MATCH_METHOD_LABEL[method] ?? method).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2">{sample.divergentFields?.join(', ') || '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
